import { Links, Meta, Outlet, Scripts, ScrollRestoration, isRouteErrorResponse } from "react-router";
import type { LinksFunction } from "react-router";
import { useEffect } from "react";
import { useLocation, useMatches } from "react-router";
import stylesheet from "./tailwind.css?url";
import { UserProvider } from "./contexts/user";
import { SettingsProvider } from "./contexts/settings";
import { DrawingSuppressProvider } from "./contexts/drawing";
import Header from "./components/layout/Header/Header";
import Footer from "./components/layout/Footer/Footer";
import Navigation from "./components/layout/Navigation/Navigation";
import DrawingLayer from "./components/drawing/DrawingLayer";
import { PageErrorFallback, NotFound } from "./components/layout/ErrorBoundary/ErrorBoundary";
import { isFaPath } from "./lib/faLocale";

export const links: LinksFunction = () => [{ rel: "stylesheet", href: stylesheet }];

// The document shell. RR injects <Meta>/<Links> from route meta()/links() and
// the client bundle via <Scripts>. Note we hydrate the whole document (see
// entry.client), so the <html> here is the hydration root.
export function Layout({ children }: { children: React.ReactNode }) {
  // The Farsi surface is the one part of the site that is not English or LTR,
  // and both facts belong on <html> rather than on a wrapper: `dir` decides how
  // Tailwind's logical properties resolve for the whole subtree, and `lang`
  // decides which font stack and hyphenation a browser picks. Read from the
  // path, so it is identical on the server and on hydration.
  const { pathname } = useLocation();
  const matches = useMatches();
  const fa = isFaPath(pathname);
  const isResume = matches.some((m) => (m.data as { isResume?: boolean } | undefined)?.isResume);
  return (
    <html lang={fa ? "fa" : "en"} dir={fa ? "rtl" : "ltr"}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
        {/* The "System design" chat bubble, a Vatan widget in Gaz's Room
            (me@gazar.dev). The token is publishable: it only talks to that one
            widget, from the origins listed on it in the Vatan console, inside its
            daily cap. How it looks is set there too, not here. Rendered in the
            SSR shell so document.currentScript is set when it runs. Kept off the
            resume and the Farsi page, whose hosts are not on its origin list. */}
        {!fa && !isResume && (
          <script
            src="https://api.vatan.one/widget.js"
            data-widget="vk_pub_P4zwXrYAILl_Bl4LprLHXwZ8IVRmrNVu"
            defer
          />
        )}
      </body>
    </html>
  );
}

export default function App() {
  const location = useLocation();
  const matches = useMatches();
  // The resume renders its own bare layout (no chrome) for clean printing. It
  // is served at the root of the resume host, so the path no longer identifies
  // it; the route loader flags it instead.
  const isResumePage = matches.some((m) => (m.data as { isResume?: boolean } | undefined)?.isResume);
  // The Farsi page ships its own RTL header and footer. The site chrome is
  // English and LTR, and mirroring a 9-item nav and a full footer for one page
  // is a lot of surface to maintain for no reader benefit, so it is suppressed
  // the same way the resume suppresses it.
  const isBare = isResumePage || isFaPath(location.pathname);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // All head tags come from route meta() (SSR). No react-helmet in the tree.
  return (
    <SettingsProvider>
      <UserProvider>
        <DrawingSuppressProvider>
          {!isBare && <Header />}
          {!isBare && <Navigation />}
          <main>
            <Outlet />
          </main>
          {!isBare && <Footer />}
          {!isBare && <DrawingLayer />}
        </DrawingSuppressProvider>
      </UserProvider>
    </SettingsProvider>
  );
}

// Root error boundary. RR renders this (inside <Layout>) instead of <App/> when
// a route loader throws or a render fails — so it must supply its own chrome.
// A 404 (thrown Response from the :page/:page/:slug loaders, or an unmatched
// URL) gets a branded, navigable NotFound; anything else keeps the generic
// error fallback. The HTTP status stays 404 (set by the thrown Response).
export function ErrorBoundary({ error }: { error: unknown }) {
  const is404 = isRouteErrorResponse(error) && error.status === 404;
  const message = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : error instanceof Error
      ? error.message
      : "Unknown error";
  return (
    <SettingsProvider>
      <UserProvider>
        <Header />
        <Navigation />
        <main>
          {is404 ? (
            <NotFound />
          ) : (
            <PageErrorFallback error={new Error(message)} resetErrorBoundary={() => window.location.reload()} />
          )}
        </main>
        <Footer />
      </UserProvider>
    </SettingsProvider>
  );
}
